import Big from "big.js";

import { SplitType } from "constants/splits";

interface SplitAllocationInput {
  businessId?: string | null;
  amount?: string | number | null;
}

export function validateSplitAllocations(
  splits: SplitAllocationInput[],
  type: SplitType,
  quantity: number | null,
): string[] {
  const splitErrors: string[] = [];

  const totalAmount = splits.reduce(
    (acc, cur) => acc.plus(new Big(Number(cur.amount) || 0)),
    new Big(0),
  );

  if (splits.some(s => !s.amount)) {
    splitErrors.push("All splits must have an amount.");
  }
  if (splits.some(s => !s.businessId)) {
    splitErrors.push("All splits must have a business.");
  }
  if (type === SplitType.PERCENTAGE && !totalAmount.eq(1)) {
    splitErrors.push(
      `Split percentages must add up to 100% (${new Big(1)
        .minus(totalAmount)
        .times(100)}% difference).`,
    );
  }
  if (type === SplitType.HEADCOUNT) {
    if (!quantity) {
      splitErrors.push("Set a head count before using headcount splits.");
    } else if (!totalAmount.eq(quantity)) {
      splitErrors.push(
        `Total split amount must equal head count (${totalAmount
          .minus(quantity)
          .abs()} difference).`,
      );
    }
  }

  return splitErrors;
}

export function validateVendorSplits(
  splits: SplitAllocationInput[],
  type: SplitType,
  quantity: number | null,
): { splits?: string[] } {
  const splitErrors = validateSplitAllocations(splits, type, quantity);
  return splitErrors.length > 0 ? { splits: splitErrors } : {};
}

export function validateBuyerSplits(
  splits: SplitAllocationInput[],
  type: SplitType,
  quantity: number | null,
): { splits?: string[] } {
  const splitErrors = validateSplitAllocations(splits, type, quantity);
  return splitErrors.length > 0 ? { splits: splitErrors } : {};
}

interface SplitKeyInterface {
  primaryBusinessId?: string;
  deploymentId?: number;
  saleLotId?: string;
  consignmentId?: string;
  livestockSaleId?: number;
}

export interface SplitLookup {
  [id: string]: VendorSplit | BuyerSplit;
}

export const getSplitKey = ({
  primaryBusinessId,
  deploymentId,
  saleLotId,
  consignmentId,
  livestockSaleId,
}: SplitKeyInterface): string =>
  [
    primaryBusinessId,
    deploymentId,
    saleLotId || consignmentId || livestockSaleId,
  ].join("|");

interface BestMatchingSplitInterface {
  splitByKeyLookup: SplitLookup;
  primaryBusinessId: string;
  deploymentId: number;
  livestockSaleId: number;
  consignmentId: string;
  saleLotId: string;
}

export function formatSplitAmount(amount: Big, type: SplitType): string {
  if (type === SplitType.HEADCOUNT) {
    return `${amount} hd `;
  }
  return `${amount.times(100)}%`;
}

export const getBestMatchingSplit = ({
  splitByKeyLookup,
  primaryBusinessId,
  deploymentId,
  livestockSaleId,
  consignmentId,
  saleLotId,
}: BestMatchingSplitInterface) => {
  // Return the best matching split using the available inputs.

  if (!splitByKeyLookup) {
    return null;
  }

  const saleLotSplitKey = getSplitKey({
    primaryBusinessId,
    deploymentId,
    saleLotId,
  });

  const consignmentSplitKey = getSplitKey({
    primaryBusinessId,
    deploymentId,
    consignmentId,
  });

  const saleSplitKey = getSplitKey({
    primaryBusinessId,
    deploymentId,
    livestockSaleId,
  });

  return (
    splitByKeyLookup[saleLotSplitKey] ||
    splitByKeyLookup[consignmentSplitKey] ||
    splitByKeyLookup[saleSplitKey]
  );
};
